import { TagsInput } from '@mantine/core';
import uniq from 'lodash/uniq';
import { useTranslation } from 'react-i18next';

import { ThemeClassNamesStringList } from 'shared/client/consts/ThemeClassNames';
import { FormField } from 'shared/components/forms/FormField';
import { FormSection } from 'shared/components/forms/FormSection';
import {
  getUniqueClassesListFromJSON,
  useAllCustomCSSClasses,
  useWidgetCustomCSSClasses
} from 'shared/utils/customCSSClasses';
import { useParentTheme } from 'shared/utils/useParentTheme';

import { useContentObject } from '../../providers/ContentObjectProvider/ContentObjectProvider';

const classNamesRegex = /\.([^\s{}:,]+)(?![^{}]*\})/g;

const getMultiSelectClassesData = (allCustomCSSClassesJSON: Record<string, string[]>, themeCSS: string) => {
  const allEditorClasses = [...themeCSS.matchAll(classNamesRegex)].map(match => match[1]);
  const customEditorClasses = allEditorClasses.filter(name => !ThemeClassNamesStringList.includes(name));
  const customWidgetsClasses = getUniqueClassesListFromJSON(allCustomCSSClassesJSON);

  return uniq([...customWidgetsClasses, ...customEditorClasses]).map(customClass => {
    return { value: customClass, label: customClass };
  });
};

export function CustomCSSClassesInput() {
  const { t } = useTranslation('widgets.customCSSClasses');
  const { id: widgetId, document } = useContentObject();

  // Two hooks are needed to achieve the proper data reactiveness:
  // first -> data to read
  const widgetAppliedCustomClasses = useWidgetCustomCSSClasses(widgetId, document) ?? [];
  // second -> map to modify
  const allCustomCSSClasses = useAllCustomCSSClasses(document);

  const onChange = (newCustomClasses: string[]) => {
    allCustomCSSClasses.set(widgetId, newCustomClasses);
  };
  const { data: parentTheme } = useParentTheme();
  const multiSelectClassesData = getMultiSelectClassesData(allCustomCSSClasses.toJSON(), parentTheme?.css ?? '');

  return (
    <FormSection label={t('label')}>
      <FormField>
        <TagsInput
          value={widgetAppliedCustomClasses}
          data={multiSelectClassesData}
          placeholder={t('placeholder')}
          onChange={onChange}
          splitChars={[',', ' ', '|']}
          data-testid="custom-css-classes-input"
          comboboxProps={{ withinPortal: false }}
        />
      </FormField>
    </FormSection>
  );
}
